import { EventEmitter, Injectable, Output } from '@angular/core';
import { GridsterConfig } from 'angular-gridster2';
import { BehaviorSubject } from 'rxjs';
import { custom } from 'devextreme/ui/dialog';
import { IBaseWidget } from '../widgets/widget/base/base-widget';
import { WidgetType } from '../widgets/widget/base/widget-list';
import { DashboardService } from 'src/api/generated';
import { MachineInfoMainService } from '../../../shared/services/machine-info.service';
import { RemoteDataWrapperService } from '../../../shared/services/remote-data/remote-data-wrapper.service';

export interface DashboardConfiguration {
  name: string;
  gridsterOptions: GridsterConfig;
  widgets: IBaseWidget[];
  layoutName?: string;
  currentMachine?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardDataService {
  // Observable for updating edit mode
  private _edit: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public edit$ = this._edit.asObservable();

  @Output() onLastWidgetInitialized: EventEmitter<void> = new EventEmitter();
  @Output() onLastWidgetDestroyed: EventEmitter<void> = new EventEmitter();
  private _initializedWidgets: number = 0;

  // Observable for updating widgets
  private _widgets: BehaviorSubject<IBaseWidget[]> = new BehaviorSubject<IBaseWidget[]>([]);
  public widgets$ = this._widgets.asObservable();

  // Full screen mode
  public fullScreenMode: boolean = false;

  // Configuration, consists all state of dashboard (Default configuration)
  private _configuration: DashboardConfiguration = {
    name: 'DefaultDashboard',
    gridsterOptions: {
      gridType: 'fit',
      draggable: {
        enabled: false
      },
      resizable: {
        enabled: false
      }
    },
    widgets: [],
    currentMachine: undefined
  };

  set configuration(conf: DashboardConfiguration) {
    this._configuration = conf;
    this._configuration.gridsterOptions.itemChangeCallback = (item, itemComponent) => {
      this.updatedashboard();
    };
    this._widgets.next([...this._configuration.widgets]);
    if (this.configuration.gridsterOptions.draggable){
      this.configuration.gridsterOptions.draggable.enabled = false;
    } else {
      this.configuration.gridsterOptions.draggable = { enabled: false };
    }
    if (this.configuration.gridsterOptions.resizable){
      this.configuration.gridsterOptions.resizable.enabled = false;
    } else {
      this.configuration.gridsterOptions.resizable = { enabled: false };
    }
    this.configuration.gridsterOptions.pushItems = false;
    this.configuration.gridsterOptions.pushResizeItems = false;

    this.configuration.gridsterOptions = { ...this.configuration.gridsterOptions };
  }

  get configuration(): DashboardConfiguration {
    return this._configuration;
  }

  constructor(
    private readonly _dashboardSvc: DashboardService,
    private readonly _miSvc: MachineInfoMainService,
    private readonly _remoteDataWrapperService: RemoteDataWrapperService
  ) {
    
  }

  private _savedAllWidgets: IBaseWidget[] = [];

  public deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  public listenOnCurrentMachineChange(): void {
    this._miSvc.currentMachine$.subscribe(machine => {
      if (machine && machine.Id !== this._configuration.currentMachine) {
        this.updatedashboard();
      }
    });
  }

  /**
   * Nastavenie widgetu na celu obrazovku
   * @param widget Widget ktory sa ma zobrazit na celu obrazovku
   */
  public fullScreenWidget(widget: IBaseWidget) {
    this._remoteDataWrapperService.turnOffCommunication();

    const sub = this.onLastWidgetInitialized.subscribe(() => {
      this._remoteDataWrapperService.turnOnCommunication();
      sub.unsubscribe();
    });

    if (this.fullScreenMode) {
      // restore
      this._widgets.next([...this._savedAllWidgets]);
    } else {
      // save
      this._savedAllWidgets = this._widgets.getValue();

      // take copy of widget (go to json and back => deep copy)
      const fsW = this.deepClone(widget);
      fsW.gridsterWidgetOptions.cols = this.configuration.gridsterOptions.maxCols ?? 1;
      fsW.gridsterWidgetOptions.rows = this.configuration.gridsterOptions.maxRows ?? 1;
      fsW.gridsterWidgetOptions.x = 0;
      fsW.gridsterWidgetOptions.y = 0;
      this._widgets.next([fsW]);
    }

    this.fullScreenMode = !this.fullScreenMode;
}

  /**
   * Nastavenie dashboard info (zakladne informacie podla ktorich vieme aky dashboard a pre koho zobrazujeme)
   * Zavola sa BE pre ziskanie konfiguracie dashboardu a nastavi sa do service
   * @param dashboardName meno dashboardu ktory sa ma zobrazit
   */
  public initDashboard(): void {
    this._dashboardSvc.getConfigurationDashboard().subscribe({
      next: (data) => {
        if (data) {
          // parse data from BE
          const tmp = {
            name: data.Name ?? '',
            widgets: (JSON.parse(data.Widgets ?? '{}') as IBaseWidget[]),
            gridsterOptions: JSON.parse(data.DasboardSettings ?? '{}'),
            layoutName: data.LayoutName ?? '',
          }

          // set id for each widget
          tmp.widgets.forEach(w => {
            if (!w.id) {
              w.id = this._getFirstUnUsedWidgetId(tmp.widgets);
            }
          });

          // set current machine
          if (data.CurrentMachine) {
            this._miSvc.getOneMachine(data.CurrentMachine).subscribe(res => {
              this.configuration = {...tmp, currentMachine: res.Id};
              this._miSvc.CurrentMachine = res;
            });
          } else {
            this.configuration = {...tmp};
          }
        }
      },
      error: (error) => {
        console.error(error);
      }
    });
  }


  /**
   * Funkcia pre pridanie noveho prazdneho widgetu
   * Funguje len ak je edit mode zapnuty a je pouzity custom layout
   */
  public addWidget(): void {
    const tmp: IBaseWidget[] = [...this._widgets.getValue()];

    tmp.push({
      id: this._getFirstUnUsedWidgetId(),
      type: WidgetType.Empty,
      title: 'Empty Widget',
      gridsterWidgetOptions: { cols: 2, rows: 2, x: 0, y: 0 },
      hasRefreshButton: false
    });

    this._widgets.next(tmp);
    this.updatedashboard();
  }

  /**
   * Funkcia pre zmazanie widgetu
   * @param widget Widget ktory sa ma zmazat
   */
  public deleteWidget(widget: IBaseWidget) {
    this.configuration.widgets = this.configuration.widgets.filter(w => w !== widget);
    this._widgets.next([...this.configuration.widgets]);
    this.updatedashboard();
  }

  /**
   * Funkcia overuje ci je mozne zmenit layout na zaklade aktualneho stavu dashboardu.
   * Pokial je mozne zmenit tak zavola _changeLayout
   * Pokial nie je mozne zmenit tak zobrazi dialog s informaciou o tom ktore widgety budu zmazane
   * @param layout Layout ktory sa ma nastavit. Layout obsahuje polozky cols, rows, x, y pre kazdy widget
   */
  public setLayout(layout: any): void {
    // check if layout is custom
    if (layout.name === 'custom') {
      if (this.configuration.gridsterOptions.draggable){
        this.configuration.gridsterOptions.draggable.enabled = true;
      } else {
        this.configuration.gridsterOptions.draggable = { enabled: true };
      }
      if (this.configuration.gridsterOptions.resizable){
        this.configuration.gridsterOptions.resizable.enabled = true;
      } else {
        this.configuration.gridsterOptions.resizable = { enabled: true };
      }
      this.configuration.gridsterOptions.pushItems = false;
      this.configuration.gridsterOptions.defaultLayerIndex = 0;
      this.configuration.layoutName = layout.name;
      this.updatedashboard();
      return;
    } else {
      if (this.configuration.gridsterOptions.draggable){
        this.configuration.gridsterOptions.draggable.enabled = false;
      } else {
        this.configuration.gridsterOptions.draggable = { enabled: false };
      }
      if (this.configuration.gridsterOptions.resizable){
        this.configuration.gridsterOptions.resizable.enabled = false;
      } else {
        this.configuration.gridsterOptions.resizable = { enabled: false };
      }
      this.configuration.gridsterOptions.pushItems = false;
      this.configuration.gridsterOptions.pushResizeItems = false;
    }

    this.configuration.gridsterOptions = { ...this.configuration.gridsterOptions };

    // check if layout is posible
    if (layout.layout.length < this.configuration.widgets.filter(w => w.type !== WidgetType.Empty).length) {

      // create list with widgets must be deleted
      let deletewidgets = this.configuration.widgets.filter((w, index) => layout.layout.length <= index).map(w => w.title);
      let deleteWidgetsString = '<li>';
      deleteWidgetsString += deletewidgets.join('</li><li>') + "</li>";

      // show dialog
      let changeLayoutDialog = custom({
        title: "Change layout",
        messageHtml: `<p>You are trying to change layout to smaller count of widgets.</p>
                      <p>Widgets will be deleted:</p>
                      <ul>
                        ${deleteWidgetsString}
                      </ul>`,
        buttons: [
          {
            text: "Ok",
            onClick: (e) => {
                return true;
            }
          },
          {
            text: "Cancel",
            onClick: (e) => {
                return false;
            }
          }
        ]
      });

      changeLayoutDialog.show().then((result: boolean) => {
        if (result) {
          this._changeLayout(layout);
        }
      });
    } else {
      this._changeLayout(layout);
    }
  }

    /**
   * Funkcia zmeni layout dashboardu podla layoutu ktory je poslany ako parameter
   * Odstrani prazdne widgety a widgety ktore sa pripadne nezmestia do layoutu
   * Prida prazdne widgety pokial je to potrebne na miesta ktore su v layoute ale nie su v ziadnom widgete
   * @param layout Layout ktory sa ma nastavit. Layout obsahuje polozky cols, rows, x, y pre kazdy widget
   */
    private _changeLayout(layout: any): void {
      // set layout name
      this.configuration.layoutName = layout.name;
      // delete empty widgets
      this.configuration.widgets = this.configuration.widgets.filter(w => w.type !== WidgetType.Empty);
      // delete widgets that are not in layout
      this.configuration.widgets = this.configuration.widgets.filter((w, index) => layout.layout.length > index);
      // update widgets
      layout.layout.forEach((layout: any, index: number) => {
        if (this.configuration.widgets.length <= index) {
          this.configuration.widgets.push({
            id: this._getFirstUnUsedWidgetId(),
            type: WidgetType.Empty,
            gridsterWidgetOptions: {
              cols: layout.cols,
              rows: layout.rows,
              x: layout.x,
              y: layout.y
            },
            hasRefreshButton: false
          });
        } else {
          this.configuration.widgets[index].gridsterWidgetOptions.cols = layout.cols;
          this.configuration.widgets[index].gridsterWidgetOptions.rows = layout.rows;
          this.configuration.widgets[index].gridsterWidgetOptions.x = layout.x;
          this.configuration.widgets[index].gridsterWidgetOptions.y = layout.y;
        }
      });

      this._widgets.next([...this.configuration.widgets]);
      this.updatedashboard();
    }

    /**
     * Function for save dashboard configuration
     * Save configuration to BE (often used after change layout or widgets)
     */
    public updatedashboard() {
      // Handle full screen mode
      if (this.fullScreenMode) {
        let oldInstancFullScreenWidget = this._savedAllWidgets.find(w => w.id === this._widgets.getValue()[0].id);
        if (oldInstancFullScreenWidget) {
          oldInstancFullScreenWidget.options = this._widgets.getValue()[0].options;
        }
        this.configuration.widgets = this._savedAllWidgets;
      } else {
        this.configuration.widgets = this._widgets.getValue();
      }

      if ((this.configuration.gridsterOptions as any)?.api?.optionsChanged) {
        (this.configuration.gridsterOptions as any).api.optionsChanged();
      }

      this._dashboardSvc.saveConfigurationDashboard({
        Name: this.configuration.name,
        Widgets: JSON.stringify(this.configuration.widgets),
        DasboardSettings: JSON.stringify(this.configuration.gridsterOptions),
        LayoutName: this.configuration.layoutName,
        CurrentMachine: this._miSvc.CurrentMachine?.Id
      }).subscribe();
    }

    /**
     * Funkcia pre nastavenie edit modu
     * @param edit Hodnota na ktoru sa ma nastavit edit mode
     */
    public setEditMode(edit: boolean): void {
      this._edit.next(edit);
    }

    /**
     * Funkcia pre zmenu edit modu
     * Ak je edit mode zapnuty tak ho vypne a naopak
     */
    public toggleEditMode(): void {
      if (this._edit.value) {
        if (this.configuration.gridsterOptions.draggable){
          this.configuration.gridsterOptions.draggable.enabled = false;
        } else {
          this.configuration.gridsterOptions.draggable = { enabled: false };
        }
        if (this.configuration.gridsterOptions.resizable){
          this.configuration.gridsterOptions.resizable.enabled = false;
        } else {
          this.configuration.gridsterOptions.resizable = { enabled: false };
        }
        this.configuration.gridsterOptions.pushItems = false;
        this.configuration.gridsterOptions.pushResizeItems = false;

        this.configuration.gridsterOptions = { ...this.configuration.gridsterOptions };
      }

      this._edit.next(this._edit.value ? false : true);
    }

    public widgetDestroyed(): void {
      this._initializedWidgets--;

      if (this._initializedWidgets === 0) {
        this.onLastWidgetDestroyed.emit();
      }
    }

    public widgetInitialized(): void {
      this._initializedWidgets++;

      if (this._initializedWidgets === this._widgets.value.filter(w => w.type === WidgetType.Chart || w.type === WidgetType.ChartCanva || w.type === WidgetType.Correctors || w.type === WidgetType.MachineView || w.type === WidgetType.AdjustPanel).length) {
        this.onLastWidgetInitialized.emit();
      }
    }

    private _getFirstUnUsedWidgetId(widgetList: IBaseWidget[] = this.configuration.widgets): string {
      let id = 0;
      while (widgetList.find(w => w.id === id.toString())) {
        id++;
      }
      return id.toString();
    }
}
